import React, { useMemo } from 'react'
import { NumericFormat } from 'react-number-format'
import { withStyles } from '@mui/styles'
import { TextField, FormControl, Tooltip, Icon, Typography } from '@mui/material'

import { InputAdornment } from '@mui/material'

import { getEndAdornment } from './number-input.utils'
import { numberInputStyles } from './number-input.styles'

/**
 * @typedef {object} NumberInputChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop name.
 * @property {string} target.value - El nuevo valor numérico como string.
 */

/**
 * Componente interno para formatear números con separadores de miles y decimales.
 * Utiliza react-number-format para proporcionar formato numérico personalizado.
 *
 * @param {object} props - Props del componente
 * @param {function} props.inputRef - Referencia al input HTML
 * @param {function} props.onChange - Callback cuando cambia el valor
 * @param {number} [props.maxValue=0] - Valor máximo permitido
 * @param {string} props.name - Nombre del campo
 */
function NumberFormatCustom(props) {
  const { inputRef, onChange, maxValue = 0, ...other } = props

  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        })
      }}
      thousandSeparator="."
      decimalSeparator=","
      isNumericString
      allowNegative={false}
      isAllowed={(values) => {
        const { formattedValue, floatValue } = values
        return formattedValue === '' || !maxValue || floatValue <= maxValue
      }}
    />
  )
}

/**
 * @typedef {object} NumberInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {boolean} [readOnly=false] - Si es true, el campo se muestra como texto de solo lectura.
 * @property {boolean} [required=false] - Indica si el campo es obligatorio.
 * @property {string | number} [value=''] - El valor actual del campo numérico.
 * @property {number} [decimalScale] - Número de decimales permitidos.
 * @property {boolean} [fixedDecimalScale=false] - Si es true, mantiene fijos los decimales mostrados.
 * @property {number} [maxValue=0] - Valor máximo permitido para el campo.
 * @property {string} [units] - Unidades de medida que se muestran al final del campo.
 * @property {string} [title] - El título o etiqueta que se muestra sobre el campo.
 * @property {string} [name] - El nombre del campo, que se incluirá en el objeto de evento del onChange.
 * @property {string} [tooltip] - Texto informativo que se muestra en un icono de ayuda junto al título.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título y valor readOnly.
 * @property {object} [InputProps] - Props adicionales para el componente TextField de Material-UI.
 * @property {function(NumberInputChangeEvent): void} onChange - La función callback que se ejecuta cuando cambia el valor.
 */

/**
 * Un componente de entrada numérica que formatea automáticamente los números con
 * separadores de miles y decimales según el formato español (punto para miles, coma para decimales).
 *
 * Soporta validación de valor máximo, escala decimal personalizada, unidades de medida,
 * y modo de solo lectura. Ideal para campos monetarios, porcentajes, y otras entradas numéricas.
 *
 * @component
 * @example
 * const MyForm = () => {
 *   const [precio, setPrecio] = useState('');
 *
 *   const handleChange = (e) => {
 *     console.log(`El campo '${e.target.name}' ahora es: ${e.target.value}`);
 *     setPrecio(e.target.value);
 *   };
 *
 *   return (
 *     <NumberInput
 *       title="Precio"
 *       name="precio"
 *       value={precio}
 *       onChange={handleChange}
 *       required
 *       decimalScale={2}
 *       fixedDecimalScale
 *       units="€"
 *       maxValue={999999}
 *       tooltip="Ingrese el precio del producto"
 *     />
 *   );
 * };
 *
 * @param {NumberInputProps} props - Las props del componente NumberInput.
 */
export const NumberInput = withStyles(numberInputStyles)(({
  classes,
  readOnly = false,
  value = '',
  decimalScale = undefined,
  fixedDecimalScale = false,
  maxValue = 0,
  units = null,
  ...props
}) => {
  const typographyVariant = props.fontSize === 'small' ? '2' : '1'

  const InputProps = useMemo(() => {
    return {
      className: classes.input,
      ...props.InputProps,
      inputComponent: (inputComponentProps) => (
        <NumberFormatCustom {...{ decimalScale, maxValue, ...inputComponentProps }} />
      ),
      endAdornment: units ? (
        <InputAdornment position="end">{getEndAdornment(units)}</InputAdornment>
      ) : null,
    }
  }, [classes.input, props.InputProps, decimalScale, maxValue, units])

  const tooltip = useMemo(() => {
    return props.tooltip ? (
      <span style={{ fontSize: 16, marginLeft: 4 }}>
        <Tooltip
          placement="top"
          title={
            <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
              {props.tooltip}
            </Typography>
          }
        >
          <Icon fontSize="inherit">help_outline</Icon>
        </Tooltip>
      </span>
    ) : null
  }, [props.tooltip])

  return (
    <FormControl className={classes.root}>
      {props.title ? (
        <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
          {props.title + (tooltip ? tooltip : '')}
          {props.required ? <span style={{ color: '#B3272C' }}> *</span> : null}
        </Typography>
      ) : null}
      {readOnly ? (
        <Typography
          variant={`body${typographyVariant}`}
          color="textSecondary"
          style={{ whiteSpace: 'pre-line' }}
        >
          {value || value === 0
            ? value.toString().replace(/\./g, ',') + (units ? ' ' + units : '')
            : '--'}
        </Typography>
      ) : (
        <TextField
          {...props}
          variant="outlined"
          value={value}
          onChange={props.onChange}
          name={props.name}
          InputProps={InputProps}
        />
      )}
    </FormControl>
  )
})
